import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RECIPIENT_CAP } from "@/config/horizonscanning/options";
import { getHorizonScanningApi, HttpError, type RecipientDto } from "./api";
import { getHorizonScanningSession, setHorizonScanningSession } from "./sessionKeys";

function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at < 0 ? "" : email.slice(at + 1).toLowerCase();
}

export function ColleaguesScreen() {
  const [params] = useSearchParams();
  const session = params.get("session") ?? getHorizonScanningSession("session") ?? "";
  const [adminEmail, setAdminEmail] = useState("");
  const [recipients, setRecipients] = useState<RecipientDto[]>([]);
  const [cap, setCap] = useState(RECIPIENT_CAP);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const list = await getHorizonScanningApi().listRecipients(session);
    setAdminEmail(list.adminEmail);
    setRecipients(list.recipients);
    setCap(list.cap);
  }

  useEffect(() => {
    if (!session) {
      setError("No session. Open the watchlist from your confirm link first.");
      return;
    }
    setHorizonScanningSession("session", session);
    refresh().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : "Could not load colleagues."),
    );
  }, [session]);

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      const adminDomain = domainOf(adminEmail);
      const inviteDomain = domainOf(email);
      if (adminDomain && inviteDomain && inviteDomain !== adminDomain) {
        setFormError(`Use the same domain as the admin (@${adminDomain}).`);
        return;
      }
      if (recipients.length >= cap) {
        setFormError(`Recipient cap is ${cap} (pending + confirmed).`);
        return;
      }
      await getHorizonScanningApi().inviteRecipient(session, email);
      setEmail("");
      await refresh();
    } catch (err: unknown) {
      setFormError(err instanceof HttpError ? err.message : err instanceof Error ? err.message : "Invite failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(target: string) {
    setBusy(true);
    setFormError(null);
    try {
      await getHorizonScanningApi().removeRecipient(session, target);
      await refresh();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Remove failed.");
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <h1 className="text-2xl font-bold text-ink">Colleagues unavailable</h1>
        <p className="mt-3 text-ink-soft">{error}</p>
        <Link className="mt-6 inline-block font-semibold text-brand-700" to="/horizonscanning/start">
          Start again
        </Link>
      </div>
    );
  }

  const adminDomain = domainOf(adminEmail);

  return (
    <div className="mx-auto w-full max-w-xl px-5 py-12">
      <p className="text-sm text-ink-muted">
        <Link className="font-semibold text-brand-700" to={`/horizonscanning/watchlist?session=${encodeURIComponent(session)}`}>
          ← Watchlist
        </Link>
      </p>
      <h1 className="mt-4 text-3xl font-extrabold text-ink">Colleagues</h1>
      <p className="mt-3 text-ink-soft">
        Invite people on @{adminDomain || "your work domain"} to receive the same digest. Each person confirms from
        their own inbox. Cap: {cap} (pending + confirmed), not counting the admin.
      </p>

      <form className="mt-8 flex flex-col gap-3" onSubmit={(e) => void onInvite(e)}>
        <label className="text-sm font-medium text-ink-soft">
          Work email
          <input
            className="r1-field mt-1"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={adminDomain ? `name@${adminDomain}` : "colleague@company.com"}
          />
        </label>
        {formError ? <p className="text-sm text-amber-800">{formError}</p> : null}
        <button type="submit" className="r1-btn-primary self-start" disabled={busy || recipients.length >= cap}>
          Send invite
        </button>
      </form>

      <section className="mt-10">
        <h2 className="text-lg font-bold">Invited ({recipients.length}/{cap})</h2>
        {recipients.length === 0 ? (
          <p className="mt-2 text-ink-soft">No colleagues yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {recipients.map((r) => (
              <li key={r.email} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <p className="font-medium text-ink">{r.email}</p>
                  <p className="text-sm text-ink-muted capitalize">{r.status}</p>
                </div>
                <button
                  type="button"
                  className="text-sm font-semibold text-brand-700 disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void onRemove(r.email)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
